/**
 * EndorsementHistory — recent-endorsements list rendered inside the
 * terminal-mode InstanceDetailDrawer for endorsement workflows.
 *
 * Ported from v1 c:/_Data/mcp/src/outlook-addin/src/components/EndorsementHistory.tsx
 * Restyle: Fluent UI Card / Badge → Tailwind div + spans. The v1 backing
 * store (`workflowStore.getRecentWorkflows`) lives in Agent E's port; until
 * that lands the component falls back to whatever rows the parent passes
 * in via the optional `rows` prop.
 *
 * TODO: make this dynamic — decide where the steps/history data comes from
 * (API endpoint, workflow artifacts, or a dedicated history store) and how
 * individual items should be rendered (e.g. carrier portal steps, diff view).
 */
import type { ActiveWorkflow } from "../types/workflow";

export interface EndorsementHistoryRow {
  instance_id: string;
  status: string;
  subject?: string | null;
  sender?: string | null;
  startedAt: string;
}

export interface EndorsementHistoryProps {
  /** Pre-supplied rows. When omitted, renders the empty state. */
  rows?: EndorsementHistoryRow[];
  /**
   * Convenience: convert ActiveWorkflow (substrate-shaped) into history
   * rows so callers can pass `useActiveInstances().rows` directly.
   */
  fromActiveInstances?: ActiveWorkflow[];
  limit?: number;
}

const STATUS_TONE: Record<
  string,
  { bg: string; text: string }
> = {
  completed: {
    bg: "bg-primary-success-50",
    text: "text-primary-success-700",
  },
  carrier_handoff_ready: {
    bg: "bg-primary-success-50",
    text: "text-primary-success-700",
  },
  paused_hitl: {
    bg: "bg-primary-warning-50",
    text: "text-primary-warning-700",
  },
  failed: {
    bg: "bg-primary-error-50",
    text: "text-primary-error-700",
  },
  cancelled: {
    bg: "bg-primary-gray-light-100",
    text: "text-primary-gray-light-600",
  },
  running: {
    bg: "bg-sunrise/20",
    text: "text-primary-warning-700",
  },
};

const FALLBACK_TONE = STATUS_TONE.running;

export function EndorsementHistory({
  rows,
  fromActiveInstances,
  limit = 5,
}: EndorsementHistoryProps) {
  const data: EndorsementHistoryRow[] =
    rows ??
    (fromActiveInstances ?? []).map((i) => ({
      instance_id: i.instance_id,
      status: i.status,
      subject: i.name ?? null,
      sender: i.region ?? i.branch ?? null,
      startedAt: i.created_at,
    }));

  const limited = data.slice(0, limit);

  if (limited.length === 0) {
    // Render nothing rather than a permanent "No endorsements processed yet."
    // — the drawer mounts this with no data source wired (see the TODO
    // above), so the empty state was a standing line of confusing chrome on
    // every completed run (operator feedback 2026-08-20). When the data feed
    // lands, a populated list renders below as before.
    return null;
  }

  return (
    <div className="mt-4" data-testid="endorsement-history">
      <p className="text-xs font-semibold text-primary-gray-light-600 mb-2">
        Recent Endorsements
      </p>
      <div className="flex flex-col gap-1.5">
        {limited.map((row) => {
          const tone = STATUS_TONE[row.status] ?? FALLBACK_TONE;
          const t = new Date(row.startedAt);
          const dateLabel = isNaN(t.getTime())
            ? row.startedAt
            : `${t.toLocaleDateString()} ${t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
          return (
            <div
              key={row.instance_id}
              className="rounded-md border border-primary-gray-light-200 bg-base-white px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex-1 text-xs font-semibold text-primary-gray-light-900 truncate">
                  {row.subject || "No subject"}
                </span>
                <span
                  className={`text-2xs font-semibold px-1.5 py-[1px] rounded ${tone.bg} ${tone.text}`}
                >
                  {row.status.replace(/_/g, " ")}
                </span>
              </div>
              <p className="text-2xs text-primary-gray-light-500 mt-1">
                {row.sender ?? "—"} · {dateLabel}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default EndorsementHistory;
